import { deleteCheckIn, restoreCheckIn } from '@/api/endpoints/check-ins';
import { ADMIN_QUERY_KEYS } from '@/api/queries/admin';
import type { PaginatedFeedResponse } from '@/types/feed';
import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { FEED_QUERY_KEYS } from './use-activity';

export function useDeleteCheckIn() {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: async (checkInId: string) => {
      return deleteCheckIn(checkInId);
    },

    onMutate: async (checkInId) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: FEED_QUERY_KEYS.all });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<
        InfiniteData<PaginatedFeedResponse>
      >(FEED_QUERY_KEYS.feed());

      // Optimistically remove the item from the feed
      queryClient.setQueryData<InfiniteData<PaginatedFeedResponse>>(
        FEED_QUERY_KEYS.feed(),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              items: page.items.filter(
                (item) => item.checkIn.id !== checkInId
              ),
            })),
          };
        }
      );

      return { previousData };
    },

    onError: (_error, _variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(
          FEED_QUERY_KEYS.feed(),
          context.previousData
        );
      }
    },

    onSettled: () => {
      // Refetch to ensure server state is in sync
      queryClient.invalidateQueries({ queryKey: FEED_QUERY_KEYS.all });
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.pendingMenuItems() });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (checkInId: string) => {
      return restoreCheckIn(checkInId);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: FEED_QUERY_KEYS.all });
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.pendingMenuItems() });
    },
  });

  return {
    deleteCheckIn: (checkInId: string) => deleteMutation.mutate(checkInId),
    restoreCheckIn: (checkInId: string) => restoreMutation.mutate(checkInId),
    isDeleting: deleteMutation.isPending,
  };
}
